import { Injectable, computed, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { catchError, of, tap } from 'rxjs';
import { BankAccountViewModel } from '../../models';
import { resolveBankAccountStatus } from './active-bank-accounts.mapper';
import { ActiveBankAccountsService } from './active-bank-accounts.service';

@Injectable()
export class ActiveBankAccountsState {
  private readonly _service = inject(ActiveBankAccountsService);

  readonly selectedId = signal<string | null>(null);
  readonly loadEnabled = signal<boolean>(false);

  readonly resource = rxResource({
    params: () => (this.loadEnabled() ? 'load' : null),
    stream: () =>
      this._service.getActiveBankAccounts().pipe(
        tap((accounts) => {
          const defaultAccount =
            accounts.find((item) => item.isDefault && item.isActive) ??
            accounts.find((item) => item.isActive);
          this.selectedId.set(defaultAccount?.id ?? null);
        }),
        catchError(() => of([])),
      ),
  });

  readonly accounts = computed(() => this.resource.value() ?? []);
  readonly loading = computed(() => this.resource.isLoading());

  readonly accountViewModels = computed<BankAccountViewModel[]>(() =>
    this.accounts()
      .map((account) => ({
        id: account.id,
        bankName: account.bankDto?.name ?? 'نامشخص',
        cardNumber: account.cardNumber ?? '',
        logoUrl: account.bankDto?.logoUrl ?? '',
        isDefault: account.isDefault ?? false,
        isActive: account.isActive ?? false,
        status: resolveBankAccountStatus(
          account.status,
          account.statusDisplayName,
        ),
      }))
      .sort((account1, account2) => {
        if (account1.isDefault === account2.isDefault) {
          return 0;
        }

        return account1.isDefault ? -1 : 1;
      }),
  );

  readonly hasAccounts = computed(() => this.accountViewModels().length > 0);

  readonly hasSelectedBankAccount = computed(() => {
    const id = this.selectedId();
    if (id === null) return false;
    return this.accounts().some((a) => a.id === id);
  });

  select(id: string): void {
    this.selectedId.set(id);
  }

  reloadActiveBankAccounts(): void {
    this.resource.reload();
  }
}
